<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Finanzen</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('finance')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        v-if="(pagination.total || 0) > 0"
                        :disabled="exporting"
                        :aria-label="exporting ? 'Export läuft' : 'Export'"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="exporting ? 'Export läuft' : 'Export'"
                        @click="exportCsv"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                    </button>
                    <button
                        id="filter-button"
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="openFiltersSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            class="stage-filter-active-dot"
                            aria-hidden="true"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getSales"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow rounded-md relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex flex-1 min-h-0 overflow-hidden">
                    <div class="flex-1 min-w-0 bg-white overflow-auto sm:overflow-hidden">
                        <template v-if="items.length > 0">
                            <div class="hidden sm:flex stage-listview-split stage-listview-split-keep-last-border">
                                <div class="stage-listview-split-header">
                                    <table class="stage-listview-split-table table-listview">
                                        <colgroup>
                                            <col style="width:24rem;min-width:24rem;">
                                            <col class="w-[11rem]">
                                            <col class="w-[10rem]">
                                            <col class="w-[9rem]">
                                            <col style="width:5rem;min-width:5rem;">
                                            <col class="w-[9rem]">
                                        </colgroup>
                                        <thead>
                                        <tr>
                                            <th class="table-listview-l-th w-[24rem] min-w-[24rem]">Position / Leistung</th>
                                            <th class="table-listview-l-th w-[11rem]">Netto</th>
                                            <th class="table-listview-l-th w-[10rem]">Brutto</th>
                                            <th class="table-listview-l-th w-[9rem]">MwSt</th>
                                            <th
                                                class="table-listview-l-th"
                                                style="width:5rem;min-width:5rem;text-align:center;padding-left:0;padding-right:0;"
                                            >
                                                Details
                                            </th>
                                            <th class="table-listview-l-th table-listview-l-th-right w-[9rem]">Abrechnung</th>
                                        </tr>
                                        </thead>
                                    </table>
                                </div>
                                <div class="stage-listview-split-body">
                                    <table class="stage-listview-split-table table-listview">
                                        <colgroup>
                                            <col style="width:24rem;min-width:24rem;">
                                            <col class="w-[11rem]">
                                            <col class="w-[10rem]">
                                            <col class="w-[9rem]">
                                            <col style="width:5rem;min-width:5rem;">
                                            <col class="w-[9rem]">
                                        </colgroup>
                                        <tbody
                                            v-for="group in displayGroups"
                                            :key="group.key"
                                            class="stage-finance-group-body"
                                        >
                                        <tr
                                            class="stage-finance-group-row"
                                        >
                                            <td class="stage-finance-group-cell">
                                                <div class="stage-finance-group-title-content">
                                                    <div
                                                        v-if="groupProjectImages(group).length > 0"
                                                        class="flex items-center mr-3 flex-shrink-0"
                                                    >
                                                        <img
                                                            v-for="(project, index) in groupProjectImages(group)"
                                                            :key="'customer-finance-group-project-' + group.key + '-' + project.id"
                                                            :src="project.image_url"
                                                            :alt="project.title || 'Projektbild'"
                                                            :title="project.title || 'Projekt'"
                                                            :class="index !== 0 ? '-ml-6' : ''"
                                                            class="h-8 w-8 rounded-sm object-cover border border-white shadow-solid"
                                                        >
                                                    </div>
                                                    <span
                                                        v-else-if="group.manual"
                                                        class="stage-finance-entry-icon"
                                                        :class="{'stage-finance-entry-icon--expense': manualFinanceGroupHasExpenses(group)}"
                                                    >
                                                        <svg-vue class="h-3.5 w-3.5" icon="font-awesome.solid.euro-sign"></svg-vue>
                                                    </span>
                                                    <span class="stage-finance-group-copy">
                                                        <span class="stage-finance-group-title-row">
                                                            <span class="stage-finance-group-title" :title="financeGroupTitle(group)">
                                                                {{ financeGroupTitle(group) }}
                                                            </span>
                                                            <identifier-badge
                                                                v-if="!group.manual && group.positionId"
                                                                :value="group.positionId"
                                                                title="Positions-ID"
                                                            ></identifier-badge>
                                                            <span
                                                                v-if="financeGroupIsCompleted(group)"
                                                                class="stage-finance-completion-badge"
                                                                :style="billingStatusBadgeStyle('paid')"
                                                                title="Leistung abgeschlossen"
                                                            >
                                                                <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                                    <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.296-7.296a1 1 0 011.408 0z" clip-rule="evenodd" />
                                                                </svg>
                                                            </span>
                                                        </span>
                                                        <span class="stage-finance-group-subline">
                                                            {{ financeGroupCountLabel(group) }}
                                                        </span>
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="stage-finance-group-summary-cell font-medium w-[11rem]">
                                                {{ formatCurrency(financeGroupSummaryAmount(group, 'net')) }}
                                            </td>
                                            <td class="stage-finance-group-summary-cell font-medium w-[10rem]">
                                                {{ formatCurrency(financeGroupSummaryAmount(group, 'gross')) }}
                                            </td>
                                            <td class="stage-finance-group-summary-cell font-medium w-[9rem]">
                                                {{ formatGroupVat(group) }}
                                            </td>
                                            <td class="stage-finance-group-empty-cell" style="width:5rem;min-width:5rem;"></td>
                                            <td class="stage-finance-group-chevron-cell w-[9rem]">
                                                <button
                                                    type="button"
                                                    class="stage-finance-group-toggle"
                                                    :aria-expanded="String(!isFinanceGroupCollapsed(group.key))"
                                                    :aria-label="isFinanceGroupCollapsed(group.key) ? 'Finanzgruppe öffnen' : 'Finanzgruppe schließen'"
                                                    @click="toggleFinanceGroup(group.key)"
                                                >
                                                    <svg-vue
                                                        class="stage-finance-group-chevron"
                                                        :class="{'stage-finance-group-chevron--collapsed': isFinanceGroupCollapsed(group.key)}"
                                                        icon="font-awesome.regular.angle-down"
                                                    ></svg-vue>
                                                </button>
                                            </td>
                                        </tr>
                                        <template v-if="!isFinanceGroupCollapsed(group.key)">
                                        <tr
                                            v-for="item in group.items"
                                            :key="'customer-finance-' + item.id"
                                            class="cursor-pointer stage-finance-group-entry-row"
                                            tabindex="0"
                                            @click="openInfo(item.id)"
                                            @keydown.enter.prevent="openInfo(item.id)"
                                            @keydown.space.prevent="openInfo(item.id)"
                                        >
                                            <td class="table-listview-l-td w-[24rem] min-w-[24rem]">
                                                <div class="flex items-center min-w-0">
                                                    <span
                                                        class="stage-finance-entry-icon"
                                                        aria-hidden="true"
                                                    >
                                                        <svg-vue class="h-3.5 w-3.5" icon="font-awesome.solid.euro-sign"></svg-vue>
                                                    </span>
                                                    <div class="min-w-0">
                                                        <div class="flex items-center gap-2 min-w-0">
                                                            <div class="text-sm text-gray-700 font-medium truncate" :title="financeGroupEntryTitle(item, group)">
                                                                {{ financeGroupEntryTitle(item, group) }}
                                                            </div>
                                                            <span
                                                                v-if="positionCompletionIsCompleted(item)"
                                                                class="stage-finance-completion-badge"
                                                                :style="billingStatusBadgeStyle('paid')"
                                                                :title="positionCompletionTooltip(item)"
                                                            >
                                                                <svg class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                                    <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.296-7.296a1 1 0 011.408 0z" clip-rule="evenodd" />
                                                                </svg>
                                                            </span>
                                                        </div>
                                                        <div class="mt-0.5 text-xs text-gray-500 truncate" :title="approvalDateLabel(item)">
                                                            {{ approvalDateLabel(item) }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-base font-medium text-gray-700 w-[11rem]">
                                                <span>{{ formatCurrency(item.customer_total_amount) }}</span>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-gray-700 w-[10rem]">
                                                <div class="text-base font-medium">{{ formatCurrency(item.customer_total_with_vat) }}</div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm font-medium text-gray-700 w-[9rem]">
                                                {{ formatVatWithRate(item) }}
                                            </td>
                                            <td
                                                class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 text-center"
                                                style="width:5rem;min-width:5rem;padding-left:0;padding-right:0;"
                                            >
                                                <div class="flex items-center justify-center w-full">
                                                    <app-tooltip
                                                        placement="bottom"
                                                        width-class="w-max max-w-xs"
                                                        class="inline-flex items-center justify-center"
                                                    >
                                                        <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.regular.circle-info"></svg-vue>
                                                        <template #content>
                                                            <div class="inline-flex flex-col text-left">
                                                                <div
                                                                    v-for="(line, lineIndex) in detailInfoTooltipLines(item)"
                                                                    :key="'customer-finance-details-tooltip-' + item.id + '-' + lineIndex"
                                                                >
                                                                    {{ line }}
                                                                </div>
                                                            </div>
                                                        </template>
                                                    </app-tooltip>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-right w-[9rem]">
                                                <div class="inline-flex items-center justify-end w-full">
                                                    <span
                                                        :style="billingStatusBadgeStyle(item.billing_status)"
                                                        class="inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium"
                                                    >
                                                        {{ billingStatusLabel(item.billing_status) }}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                        </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="!loading">
                            <empty-state />
                        </template>
                    </div>

                    <aside
                        :aria-hidden="!filtersSidebar"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                        :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row mb-5">
                                <button
                                    class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                    type="button"
                                    @click="changeSort"
                                >
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="getSales"
                                    >
                                        <option value="id">ID</option>
                                        <option value="position">Position</option>
                                        <option value="effort">Aufwände</option>
                                        <option value="approved_at">Freigabetermin</option>
                                        <option value="status">Status</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>

                            <div class="stage-filter-label">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        v-model.lazy="filters.search"
                                        class="stage-filter-input form-input block pl-10 text-sm"
                                        :placeholder="$t('Search')"
                                        @change="getSales"
                                    >
                                </div>
                            </div>
                            <div class="stage-filter-label">Freigabezeitraum</div>
                            <div class="mb-3">
                                <input-select
                                    id="finance-approval-year"
                                    v-model="approvalYearFilterValue"
                                    class="stage-filter-select"
                                    :options="approvalYearOptions"
                                    option-label="name"
                                    @change="onApprovalYearFilterChange"
                                />
                            </div>

                            <div class="stage-filter-label">Abrechnungsstatus</div>
                            <div class="mb-3">
                                <input-select
                                    id="finance-billing-status"
                                    v-model="billingStatusFilterValue"
                                    class="stage-filter-select"
                                    :options="billingStatusOptions"
                                    option-label="name"
                                    @change="onBillingStatusFilterChange"
                                />
                            </div>

                            <div class="stage-filter-label">Positionen pro Seite</div>
                            <div class="mb-3">
                                <input-select
                                    id="finance-per-page"
                                    v-model="perPage"
                                    class="stage-filter-select"
                                    :options="perPageOptions"
                                    option-label="name"
                                    @change="onPerPageFilterChange"
                                />
                            </div>
                            <div v-if="hasActiveFilters" class="col-span-3">
                                <button
                                    id="finance-filter-reset-btn"
                                    type="button"
                                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                    @click="resetFilters"
                                >
                                    {{ $t('Reset selection') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

                <div v-if="items.length > 0" class="stage-finance-summary-footer">
                    <div class="stage-finance-summary-grid">
                        <div class="stage-finance-summary-item stage-finance-summary-item--label text-sm leading-6 inline-flex items-center whitespace-nowrap min-w-max text-gray-700">
                            <svg
                                class="h-4 w-4 mr-2 text-gray-500 flex-shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect x="5" y="3" width="14" height="18" rx="2" stroke="currentColor" stroke-width="1.6"/>
                                <rect x="8" y="6" width="8" height="3" rx="0.5" stroke="currentColor" stroke-width="1.4"/>
                                <path d="M8 12H10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M12 12H14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M8 15H10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M12 15H14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M8 18H10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                <path d="M12 18H16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                            </svg>
                            <span class="font-semibold">Summe</span>
                        </div>
                        <div class="stage-finance-summary-item stage-finance-summary-item--value text-sm leading-6 whitespace-nowrap min-w-max text-gray-700">
                            <span class="font-medium">{{ formatCurrency(sumCustomerTotalAmount) }}</span>
                            <span class="ml-1 text-gray-500">(Netto)</span>
                        </div>
                        <div class="stage-finance-summary-item stage-finance-summary-item--value text-sm leading-6 whitespace-nowrap min-w-max text-gray-700">
                            <span class="font-medium">{{ formatCurrency(sumCustomerTotalWithVat) }}</span>
                            <span class="ml-1 text-gray-500">(Brutto)</span>
                        </div>
                        <div class="stage-finance-summary-item stage-finance-summary-item--value text-sm leading-6 whitespace-nowrap min-w-max text-gray-700">
                            <span class="font-medium">{{ formatCurrency(sumCustomerVatAmount) }}</span>
                            <span class="ml-1 text-gray-500">(MwSt)</span>
                        </div>
                    </div>
                </div>

                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>

        <div v-show="detailModal" class="app-dialog-root fixed z-50 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div v-show="detailModal" class="app-dialog-overlay fixed inset-0 z-40 transition-opacity" @click="detailModal = false">
                    <div class="app-dialog-backdrop absolute inset-0"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <div v-show="detailModal" data-dialog-kind="special" class="app-dialog-panel relative z-50 inline-block align-bottom bg-white rounded-sm text-left overflow-hidden shadow-none transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <button
                        class="absolute top-4 right-4 z-10 inline-flex items-center justify-center h-8 w-8 rounded p-1 text-gray-400 hover:text-gray-600"
                        type="button"
                        aria-label="Close"
                        @click="detailModal = false"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.times-light"></svg-vue>
                    </button>
                    <div class="bg-white p-8">
                        <div class="mt-2">
                            <div class="text-xs font-bold uppercase tracking-wider text-gray-600">Netto</div>
                            <div class="mt-1 text-4xl leading-none font-semibold text-gray-900">
                                {{ formatCurrency(detail.customer_total_amount) }}
                            </div>
                            <div class="mt-2 text-sm text-gray-500">
                                MwSt: {{ formatCurrency(detail.customer_vat_amount) }} ({{ Number(detail.vat_rate || 0).toFixed(2) }}%)
                            </div>
                        </div>

                        <div class="mt-4 border border-bordergray-100 rounded bg-white overflow-y-auto max-h-[52vh]">
                            <div v-if="hasDisplayValue(detail.position_id)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Position-ID</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.position_id }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.position_title)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Position</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.position_title }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.service_category_name)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Leistung</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.service_category_name }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_hours)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Aufwände</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatHours(detail.customer_hours) }}</div>
                            </div>
                            <div v-if="hasQuantification(detail)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Quantifizierung</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDetailQuantification(detail) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_total_amount)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Netto</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatCurrency(detail.customer_total_amount) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_vat_amount)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">MwSt</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatCurrency(detail.customer_vat_amount) }} ({{ Number(detail.vat_rate || 0).toFixed(2) }}%)</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.customer_approved_at)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Freigabe</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDate(detail.customer_approved_at) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.billed_at)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Abgerechnet</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDate(detail.billed_at) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.invoice_number)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Rechnungsnummer</div>
                                <div class="text-sm text-gray-900 text-right">{{ detail.invoice_number }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.paid_at)" class="px-4 py-3 border-b border-bordergray-100 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Bezahlt</div>
                                <div class="text-sm text-gray-900 text-right">{{ formatDate(detail.paid_at) }}</div>
                            </div>
                            <div v-if="hasDisplayValue(detail.billing_status)" class="px-4 py-3 flex items-center justify-between gap-6">
                                <div class="text-sm text-gray-500">Abrechnung</div>
                                <div class="text-sm text-gray-900 text-right">{{ billingStatusLabel(detail.billing_status) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {UI_COLORS} from '@/utils/ui-colors';

import {
    queryHasKey,
    queryNumber,
    queryString,
    queryWithoutKeys,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: "finance-customer-list",
    metaInfo() {
        return {
            title: "Finanzen"
        }
    },
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const allowedSortColumns = ['id', 'position', 'effort', 'approved_at', 'status'];
        const initialSortColumn = queryString(routeQuery, 'list_sort_column', 'approved_at');
        const initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc') === 'asc' ? 'asc' : 'desc';
        const initialBillingStatus = queryString(
            routeQuery,
            queryHasKey(routeQuery, 'list_billing_status') ? 'list_billing_status' : 'billing_status',
            ''
        ).toLowerCase();
        const initialApprovalYear = queryNumber(
            routeQuery,
            queryHasKey(routeQuery, 'list_approval_year') ? 'list_approval_year' : 'approval_year',
            0
        );

        return {
            loading: false,
            filtersSidebar: false,
            page: queryNumber(routeQuery, 'list_page', 1),
            perPage: queryNumber(routeQuery, 'list_per_page', 100),
            sort: {
                column: allowedSortColumns.includes(initialSortColumn) ? initialSortColumn : 'approved_at',
                order: initialSortOrder,
            },
            filters: {
                search: queryString(routeQuery, 'list_search', null),
                provider_id: queryNumber(routeQuery, 'list_provider_id', 0) > 0 ? queryNumber(routeQuery, 'list_provider_id', 0) : null,
                billing_status: ['open', 'billed', 'paid', 'reversed'].includes(initialBillingStatus) ? initialBillingStatus : null,
                approval_year: initialApprovalYear > 0 ? initialApprovalYear : null,
                overview: 'all',
            },
            pagination: {
                currentPage: 1,
                perPage: 100,
                total: 0,
                totalPages: 1,
                entryTotal: 0,
                unit: 'groups',
            },
            summary: {
                net: 0,
                vat: 0,
                gross: 0,
            },
            overview: {
                all_count: 0,
            },
            approvalYears: [],
            providers: [],
            items: [],
            groupSummaries: {},
            collapsedFinanceGroups: {},
            exporting: false,
            detailModal: false,
            detail: {},
            settlementStatuses: [
                {value: 'open', label: 'Offen', color: UI_COLORS.text.subtle},
                {value: 'billed', label: 'Abgerechnet', color: '#2563EB'},
                {value: 'paid', label: 'Bezahlt', color: '#11A54D'},
                {value: 'reversed', label: 'Storniert', color: '#EF4444'},
            ],
        };
    },
    computed: {
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        overviewFilterValue: {
            get() {
                return 'all';
            },
            set() {
                this.filters.overview = 'all';
            },
        },
        billingStatusFilterValue: {
            get() {
                return this.filters.billing_status || 'all';
            },
            set(value) {
                this.filters.billing_status = value === 'all' ? null : value;
            },
        },
        approvalYearFilterValue: {
            get() {
                return this.filters.approval_year ? Number(this.filters.approval_year) : 0;
            },
            set(value) {
                const approvalYear = Number(value || 0);
                this.filters.approval_year = approvalYear > 0 ? approvalYear : null;
            },
        },
        providerFilterValue: {
            get() {
                return this.filters.provider_id ? Number(this.filters.provider_id) : 0;
            },
            set(value) {
                const providerId = Number(value || 0);
                this.filters.provider_id = providerId > 0 ? providerId : null;
            },
        },
        overviewOptions() {
            return [
                {id: 'all', name: `Alle Rechnungen (${this.overview.all_count || 0})`},
            ];
        },
        billingStatusOptions() {
            const options = [{id: 'all', name: 'Alle'}];
            (this.settlementStatuses || []).forEach((status) => {
                options.push({
                    id: status.value,
                    name: status.label,
                });
            });
            return options;
        },
        providerFilterOptions() {
            const providerOptions = (this.providers || []).map((provider) => ({
                id: Number(provider.id),
                name: `${provider.name} (${provider.count || 0})`,
                avatarUrl: this.getUserAvatar(provider),
            }));
            return [{id: 0, name: 'Alle', avatarUrl: ''}, ...providerOptions];
        },
        displayGroups() {
            const groups = [];
            const groupIndexes = {};
            (this.items || []).forEach((item) => {
                const positionId = Number(item?.position_id || 0);
                const key = positionId > 0 ? `position:${positionId}` : 'manual';
                if (groupIndexes[key] === undefined) {
                    groupIndexes[key] = groups.length;
                    groups.push({
                        key,
                        manual: positionId < 1,
                        positionId: positionId > 0 ? positionId : null,
                        items: [],
                    });
                }

                groups[groupIndexes[key]].items.push(item);
            });

            return groups;
        },
        approvalYearOptions() {
            const options = [{id: 0, name: 'Alle'}];
            (this.approvalYears || []).forEach((year) => {
                options.push({
                    id: Number(year),
                    name: String(year),
                });
            });
            return options;
        },
        perPageOptions() {
            const defaults = [25, 50, 100, 200];
            const current = Number(this.perPage || 0);
            const values = defaults.includes(current) ? defaults : [...defaults, current].sort((a, b) => a - b);
            return values.map((value) => ({id: value, name: value}));
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || Number(this.filters.provider_id || 0) > 0
                || (this.filters.billing_status || null) !== null
                || Number(this.filters.approval_year || 0) > 0
                || this.sort.column !== 'approved_at'
                || this.sort.order !== 'desc'
                || Number(this.perPage || 0) !== 100;
        },
        sumCustomerTotalAmount() {
            return Number(this.summary?.net || 0);
        },
        sumCustomerVatAmount() {
            return Number(this.summary?.vat || 0);
        },
        sumCustomerTotalWithVat() {
            return Number(this.summary?.gross || 0);
        },
    },
    mounted() {
        this.getFilters();
        this.getSales();
    },
    methods: {
        defaultSettlementStatuses() {
            return [
                {value: 'open', label: 'Offen', color: UI_COLORS.text.subtle},
                {value: 'billed', label: 'Abgerechnet', color: '#2563EB'},
                {value: 'paid', label: 'Bezahlt', color: '#11A54D'},
                {value: 'reversed', label: 'Storniert', color: '#EF4444'},
            ];
        },
        normalizeHexColor(color, fallback) {
            const normalized = String(color || '').trim().toUpperCase();
            if (/^#[0-9A-F]{6}$/.test(normalized)) {
                if (['#10B981', '#00A944'].includes(normalized)) {
                    return '#11A54D';
                }

                return normalized;
            }

            const normalizedFallback = String(fallback || UI_COLORS.text.subtle).toUpperCase();
            return ['#10B981', '#00A944'].includes(normalizedFallback) ? '#11A54D' : normalizedFallback;
        },
        normalizeSettlementStatuses(statuses) {
            const fallbackMap = this.defaultSettlementStatuses().reduce((carry, item) => {
                carry[item.value] = item;
                return carry;
            }, {});

            const incomingMap = (Array.isArray(statuses) ? statuses : []).reduce((carry, item) => {
                const value = String((item || {}).value || '').toLowerCase();
                if (['open', 'billed', 'paid', 'reversed'].includes(value)) {
                    carry[value] = item || {};
                }
                return carry;
            }, {});

            return ['open', 'billed', 'paid', 'reversed'].map((value) => {
                const incoming = incomingMap[value] || {};
                const fallback = fallbackMap[value];
                return {
                    value,
                    label: String(incoming.label || fallback.label),
                    color: this.normalizeHexColor(incoming.color || fallback.color, fallback.color),
                };
            });
        },
        settlementStatusMeta(status) {
            const normalized = String(status || '').toLowerCase();
            const fallback = this.defaultSettlementStatuses()[0];
            return (this.settlementStatuses || []).find((item) => item.value === normalized) || fallback;
        },
        hexToRgba(hex, alpha) {
            const normalized = this.normalizeHexColor(hex, UI_COLORS.text.subtle).replace('#', '');
            const r = parseInt(normalized.slice(0, 2), 16);
            const g = parseInt(normalized.slice(2, 4), 16);
            const b = parseInt(normalized.slice(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        },
        openFiltersSidebar() {
            this.filtersSidebar = !this.filtersSidebar;
        },
        toggleFinanceGroup(groupKey) {
            this.collapsedFinanceGroups[groupKey] = !this.isFinanceGroupCollapsed(groupKey);
        },
        isFinanceGroupCollapsed(groupKey) {
            return this.collapsedFinanceGroups[groupKey] === true;
        },
        groupProjectImages(group) {
            const projects = [];
            const projectKeys = new Set();
            (group?.items || []).forEach((item) => {
                this.projectImages(item).forEach((project) => {
                    const key = String(project.id || project.image_url || '');
                    if (key !== '' && !projectKeys.has(key)) {
                        projectKeys.add(key);
                        projects.push(project);
                    }
                });
            });

            return projects;
        },
        financeGroupTitle(group) {
            if (group?.manual) {
                return 'Manuelle Finanzeinträge';
            }

            const firstItem = group?.items?.[0] || null;
            return firstItem?.position_title
                || (group?.positionId ? `Position ${group.positionId}` : '-');
        },
        financeGroupCountLabel(group) {
            const count = Array.isArray(group?.items) ? group.items.length : 0;
            return `${count} ${count === 1 ? 'Finanzeintrag' : 'Finanzeinträge'}`;
        },
        financeGroupSummaryAmount(group, amountKey) {
            const summary = this.groupSummaries?.[group?.key] || null;
            return Number(summary?.[amountKey] || 0);
        },
        formatVatRate(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }

            const rate = Number(value);
            if (!Number.isFinite(rate)) {
                return '-';
            }

            return `${rate.toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}%`;
        },
        financeGroupVatRateLabel(group) {
            const rates = [];
            (group?.items || []).forEach((item) => {
                if (item?.vat_rate === null || item?.vat_rate === undefined || item?.vat_rate === '') {
                    return;
                }

                const rate = Number(item.vat_rate);
                if (Number.isFinite(rate) && !rates.some((existingRate) => Math.abs(existingRate - rate) < 0.0001)) {
                    rates.push(rate);
                }
            });

            if (rates.length === 1) {
                return this.formatVatRate(rates[0]);
            }
            if (rates.length > 1) {
                return 'verschiedene';
            }
            return '';
        },
        formatGroupVat(group) {
            const amount = this.formatCurrency(this.financeGroupSummaryAmount(group, 'vat'));
            const rateLabel = this.financeGroupVatRateLabel(group);
            return rateLabel === '' ? amount : `${amount} (${rateLabel})`;
        },
        positionCompletionIsCompleted(item) {
            const completion = item?.position_completion || null;
            return !!(completion && completion.applicable && completion.is_completed);
        },
        positionCompletionTooltip(item) {
            const completion = item?.position_completion || null;
            const completedAt = this.formatDate(completion?.completed_at);
            return completedAt === '-'
                ? 'Leistung abgeschlossen'
                : `Leistung abgeschlossen am ${completedAt}`;
        },
        financeGroupIsCompleted(group) {
            const positionItems = (group?.items || []).filter((item) => item?.position_completion?.applicable);
            return positionItems.length > 0
                && positionItems.every((item) => this.positionCompletionIsCompleted(item));
        },
        manualFinanceGroupHasExpenses(group) {
            return !!group?.manual && (group.items || []).some((item) => (
                this.financeFlowDirection(item) === 'expense'
            ));
        },
        getFilters() {
            const self = this;
            axios.get('api/finance/filters').then(function (response) {
                self.overview = response.data.overview || self.overview;
                self.approvalYears = Array.isArray(response.data.approval_years) ? response.data.approval_years : [];
                self.providers = response.data.providers || [];
                self.settlementStatuses = self.normalizeSettlementStatuses(response.data.settlement_statuses || self.settlementStatuses);
            });
        },
        getSales() {
            const self = this;
            self.syncFinanceListRouteState();
            self.loading = true;
            axios.get('api/finance', {
                params: {
                    page: self.page,
                    perPage: self.perPage,
                    sort: JSON.stringify(self.sort),
                    search: self.filters.search,
                    provider_id: self.filters.provider_id,
                    approval_year: self.filters.approval_year,
                    settlement_status: self.filters.billing_status,
                    group_by_position: 1,
                }
            }).then(function (response) {
                self.items = response.data.items || [];
                self.groupSummaries = response.data.group_summaries || {};
                self.pagination = response.data.pagination || self.pagination;
                self.summary = response.data.summary || {
                    net: 0,
                    vat: 0,
                    gross: 0,
                };
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        syncFinanceListRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_provider_id',
                'list_billing_status',
                'list_approval_year',
                'billing_status',
                'approval_year',
            ];

            let nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_page', Number(this.page || 1) === 1 ? null : Number(this.page || 1));
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 0) === 100 ? null : Number(this.perPage || 0));
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column === 'approved_at' ? null : this.sort.column);
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order === 'desc' ? null : this.sort.order);
            setQueryValue(nextQuery, 'list_search', (this.filters.search || '').trim() || null);
            setQueryValue(nextQuery, 'list_provider_id', Number(this.filters.provider_id || 0) > 0 ? Number(this.filters.provider_id) : null);
            setQueryValue(nextQuery, 'list_billing_status', this.filters.billing_status || null);
            setQueryValue(nextQuery, 'list_approval_year', Number(this.filters.approval_year || 0) > 0 ? Number(this.filters.approval_year) : null);

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        },
        openInfo(effortId) {
            const self = this;
            self.loading = true;
            axios.get('api/finance/' + effortId).then(function (response) {
                self.detail = response.data || {};
                self.detailModal = true;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.getSales();
            }
        },
        changeSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.getSales();
        },
        onOverviewFilterChange() {
            this.page = 1;
            this.getSales();
        },
        onProviderFilterChange() {
            this.page = 1;
            this.getSales();
        },
        onBillingStatusFilterChange() {
            this.page = 1;
            this.getSales();
        },
        onApprovalYearFilterChange() {
            this.page = 1;
            this.getSales();
        },
        onPerPageFilterChange() {
            this.page = 1;
            this.getSales();
        },
        resetFilters() {
            this.filters.search = null;
            this.filters.provider_id = null;
            this.filters.billing_status = null;
            this.filters.approval_year = null;
            this.filters.overview = 'all';
            this.sort.column = 'approved_at';
            this.sort.order = 'desc';
            this.perPage = 100;
            this.page = 1;
            this.getSales();
        },
        exportCsv() {
            const self = this;
            self.exporting = true;
            axios.get('api/finance/export', {
                params: {
                    sort: JSON.stringify(self.sort),
                    search: self.filters.search,
                    provider_id: self.filters.provider_id,
                    approval_year: self.filters.approval_year,
                    settlement_status: self.filters.billing_status,
                },
                responseType: 'blob',
            }).then(function (response) {
                const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8;'});
                const link = document.createElement('a');
                const contentDisposition = response.headers && response.headers['content-disposition']
                    ? String(response.headers['content-disposition'])
                    : '';
                const match = contentDisposition.match(/filename=\"?([^\"]+)\"?/);
                const filename = match && match[1] ? match[1] : 'finanzen_kunde_export.csv';
                link.href = window.URL.createObjectURL(blob);
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
                self.exporting = false;
            }).catch(function () {
                self.exporting = false;
            });
        },
        formatHours(value) {
            return Number(value || 0).toFixed(2) + ' Std.';
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(Number(value || 0));
        },
        formatHourlyRate(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }
            return this.formatCurrency(value) + ' / Std.';
        },
        formatDate(value) {
            if (!value) {
                return '-';
            }
            return moment(value).format('DD.MM.YYYY');
        },
        formatVatWithRate(item) {
            const amount = this.formatCurrency(item?.customer_vat_amount);
            const rate = this.formatVatRate(item?.vat_rate);
            return rate === '-' ? amount : `${amount} (${rate})`;
        },
        approvalDateLabel(item) {
            return `Freigabe: ${this.formatDate(item?.customer_approved_at)}`;
        },
        hasDisplayValue(value) {
            if (value === null || value === undefined) {
                return false;
            }
            if (typeof value === 'string') {
                return value.trim() !== '';
            }
            return true;
        },
        hasQuantification(item) {
            return Number(item?.quantification_factor || 0) > 0;
        },
        detailInfoLines(item) {
            const lines = [];
            const billedDate = this.formatDate(item ? item.billed_at : null);
            const paidDate = this.formatDate(item ? item.paid_at : null);
            const invoiceNumber = String(item && item.invoice_number ? item.invoice_number : '').trim();

            if (billedDate !== '-') {
                lines.push(`Abgerechnet am: ${billedDate}`);
            }

            if (paidDate !== '-') {
                lines.push(`Bezahlt am: ${paidDate}`);
            }

            if (invoiceNumber !== '') {
                lines.push(`Rechnungsnummer: ${invoiceNumber}`);
            }

            return lines;
        },
        detailInfoTooltipLines(item) {
            const lines = this.detailInfoLines(item);
            if (lines.length > 0) {
                return lines;
            }
            return ['Abrechnung ausstehend'];
        },
        getUserAvatar(user) {
            if (!user) {
                return '';
            }
            return user.avatar && user.avatar !== 'gravatar' ? user.avatar : user.gravatar;
        },
        financeGroupEntryTitle(item, group) {
            if (group && !group.manual) {
                return item?.service_category_name || '-';
            }

            return item?.position_title || item?.title || '-';
        },
        projectImages(item) {
            return Array.isArray(item?.projects)
                ? item.projects.filter((project) => project && project.image_url)
                : [];
        },
        formatQw(value) {
            const numeric = Number(value || 0);
            const normalized = numeric > 1 && numeric <= 100 ? (numeric / 100) : numeric;
            return normalized.toFixed(2);
        },
        formatQfInteger(value) {
            const numeric = Number(value);
            if (!Number.isFinite(numeric) || numeric === 0) {
                return '0';
            }
            return String(Math.round(numeric));
        },
        formatDetailQuantification(detail) {
            const factor = Number(detail && detail.quantification_factor);
            if (!Number.isFinite(factor) || factor <= 0) {
                return 'Keine';
            }

            const value = Number(detail && detail.quantification_value);
            const normalizedValue = Number.isFinite(value) ? value : 0.25;
            const quantificationText = `QF ${Math.round(factor)} / QW ${normalizedValue.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
            const baseAmount = Number(detail && detail.customer_base_amount);
            const totalAmount = Number(detail && detail.customer_total_amount);
            const quantificationAmount = Number.isFinite(baseAmount) && Number.isFinite(totalAmount)
                ? Math.round((totalAmount - baseAmount) * 100) / 100
                : 0;
            const quantificationAmountLabel = quantificationAmount > 0
                ? ` (${this.formatCurrency(quantificationAmount)})`
                : '';

            return `${quantificationText}${quantificationAmountLabel}`;
        },
        billingStatusLabel(status) {
            return this.settlementStatusMeta(status).label;
        },
        billingStatusBadgeStyle(status) {
            const meta = this.settlementStatusMeta(status);
            const normalizedColor = this.normalizeHexColor(meta.color, UI_COLORS.text.subtle);
            if (meta.value === 'billed') {
                return {
                    color: 'rgb(255 255 255)',
                    backgroundColor: this.hexToRgba(normalizedColor, 0.78),
                    borderColor: this.hexToRgba(normalizedColor, 0.35),
                };
            }

            if (normalizedColor === '#11A54D') {
                return {
                    color: 'rgb(255 255 255)',
                    backgroundColor: 'rgb(17 165 77 / 75%)',
                    borderColor: 'rgb(3 120 50 / 30%)',
                };
            }

            if (meta.value === 'open') {
                return {
                    color: this.hexToRgba(normalizedColor, 0.95),
                    backgroundColor: '#FFFFFF',
                    borderColor: this.hexToRgba(normalizedColor, 0.28),
                };
            }

            return {
                color: this.hexToRgba(normalizedColor, 0.95),
                backgroundColor: this.hexToRgba(normalizedColor, 0.12),
                borderColor: this.hexToRgba(normalizedColor, 0.28),
            };
        },
    }
}
</script>
